import { type Theme } from "@mui/material/styles";

import { type ComponentOverrides } from "theme/theme.types";

/** `link/{size}/underline` and `link/{size}/stroke`: one value at both sizes. */
export const LINK_UNDERLINE_THICKNESS = 1;
export const LINK_FOCUS_STROKE = 2;

/**
 * The step each colour takes on hover. Keyed on MUI's colour names, which is what reaches the entry:
 * `ui/link` hands Figma's grey `inherit` over as `textSecondary`.
 */
export const linkHoverColor = (theme: Theme, color: unknown) => {
  switch (color) {
    case "primary":
      return theme.palette.primary.dark;
    case "error":
      return theme.palette.error.dark;
    case "textSecondary":
      return theme.palette.text.primary;
    default:
      return undefined;
  }
};

/**
 * The resting colour is MUI's own — `Typography`'s `color` — so only what changes is stated here.
 *
 * MUI draws `underline="always"` in `--Link-underlineColor`, `alpha(main, 0.4)`; Figma's line is the
 * text's own colour, so the variable is repointed rather than the declaration restated.
 */
export const MuiLink: ComponentOverrides["MuiLink"] = {
  styleOverrides: {
    root: ({ theme, ownerState }) => ({
      "--Link-underlineColor": "currentColor",
      textDecorationThickness: `${LINK_UNDERLINE_THICKNESS}px`,
      "&:hover": { color: linkHoverColor(theme, ownerState.color) },
      // `.Mui-focusVisible` as well: MUI tracks keyboard focus itself for `component="button"`.
      "&:focus-visible, &.Mui-focusVisible": {
        outline: `${LINK_FOCUS_STROKE}px solid currentColor`,
        outlineOffset: 0,
      },
    }),
  },
};
